/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react'
import { Button, Card, Col, Container, Row, Table, Form } from 'react-bootstrap'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import api from '../../config/AxiosInstance'
import toast, { Toaster } from 'react-hot-toast'
import Loading from '../../components/atoms/Loading'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { BadgeStatusPrList } from '../../components/atoms/StatusLabel'
import { firstLetterUpperCase } from '../../utils/TextFormatter'
import { convertDateWithTime } from '../../helpers/constants'

const headerTitleStyle = {
  color: 'var(--primary-orange)',
  fontSize: '24px',
  fontWeight: '600'
}

const tableHeaderStyle = {
  fontSize: '14px',
  fontWeight: '600',
  color: '#333333',
  letterSpacing: '0.5px',
  backgroundColor: '#f8f9fa',
  borderBottom: '2px solid #e9ecef'
}

const cardFilterStyle = {
  border: 'none',
  boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
  borderRadius: '12px',
  padding: '1.5rem',
  marginBottom: '1.5rem',
  backgroundColor: '#fff'
}

const labelStyle = {
  fontSize: '0.75rem',
  fontWeight: '700',
  color: '#999',
  textTransform: 'uppercase',
  marginBottom: '0.25rem',
  letterSpacing: '0.5px'
}

const valueStyle = {
  fontSize: '0.9rem',
  fontWeight: '500',
  color: '#212529',
  minHeight: '24px'
}

function DetailReplenishmentPage() {
  const { state } = useLocation()
  const { id } = useParams()
  const navigate = useNavigate()
  const [collapsible, setCollapsible] = useState({})
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState({
    page: false,
    submit: false
  })

  const fetchData = async () => {
    try {
      setLoading((prevLoading) => ({ ...prevLoading, page: true }))
      const res = await api.get(`replenish/${id}`)
      const { data: dataResponse } = res.data
      setData(dataResponse)
      setCollapsible((prev) =>
        dataResponse?.items?.reduce((acc, item) => {
          acc[item.rack_id] = true
          return acc
        }, {})
      )
    } catch (error) {
      const errMsg =
        error?.response?.data?.errors?.message ||
        error?.message ||
        'An error occurred'
      toast.error(errMsg, {
        position: 'top-right',
        style: {
          fontSize: '14px',
          fontWeight: '600'
        }
      })
      navigate('/replenishment')
    } finally {
      setLoading((prevLoading) => ({ ...prevLoading, page: false }))
    }
  }

  const columnRacks = [
    { key: 'batch', label: 'Batch' },
    {
      key: 'item_info',
      label: 'Item Code - Name',
      component: (value) =>
        `${value?.item_code || ''} - ${value?.item_name || ''}`
    },
    { key: 'rack', label: 'Source Rack' },
    { key: 'pic', label: 'PIC' },
    {
      key: 'action',
      label: 'Action',
      className: 'd-flex justify-content-end',
      component: (value) => (
        <div key={value?.rack_id} className="d-flex justify-content-end">
          <Button
            variant="transparent"
            className="btn-secondary"
            size="md"
            onClick={() => {
              setCollapsible((prev) => ({
                ...prev,
                [value.rack_id]: !prev[value.rack_id]
              }))
            }}
          >
            {collapsible[value.rack_id] ? (
              <ChevronUp size={16} />
            ) : (
              <ChevronDown size={16} />
            )}
          </Button>
        </div>
      )
    }
  ]

  const columnSlots = [
    { key: 'palet_id', label: 'Pallet' },
    { key: 'source_slot', label: 'Source' },
    { key: 'destination_slot', label: 'Destination' },
    {
      key: 'status',
      label: 'Status',
      component: (value) => (
        <div key={value?.id}>
          <BadgeStatusPrList
            text={
              value?.status
                ? value?.status?.toLowerCase().replace('_', ' ')
                : 'done'
            }
          />
        </div>
      )
    }
  ]

  useEffect(() => {
    fetchData()
  }, [id])

  if (loading.page) {
    return <Loading />
  }

  const renderInfoItem = (label, value, col = 3) => (
    <Form.Group className={`mb-3 col-12 col-md-${col}`}>
      <Form.Label style={labelStyle}>{label}</Form.Label>
      <div
        className="px-3 py-2 rounded"
        style={{ ...valueStyle, backgroundColor: '#e9ecef', color: '#495057' }}
      >
        {value || '-'}
      </div>
    </Form.Group>
  )

  return (
    <>
      <Toaster />
      <Container fluid className="px-4 mb-5">
        <Row className="mb-4">
          <Col
            md={12}
            className="d-flex justify-content-between align-items-start"
          >
            <div className="d-flex flex-column justify-content-start">
              <h4
                style={headerTitleStyle}
                className="d-flex align-items-center gap-2"
              >
                {data?.code || ''}{' '}
                <sup style={{ fontSize: '12px', marginLeft: '5px' }}>
                  <BadgeStatusPrList text={data?.status || 'done'} />
                </sup>
              </h4>
              <nav aria-label="breadcrumb">
                <ol className="breadcrumb fs-6">
                  <li className="breadcrumb-item">
                    <Link
                      to="/replenishment"
                      state={{
                        q: state?.q,
                        warehouse_id: state?.warehouse_id,
                        status: state?.status
                      }}
                      className="text-decoration-none"
                    >
                      <span className="text-black fw-bold text-uppercase">
                        replenishment
                      </span>
                    </Link>
                  </li>
                  <li
                    className="breadcrumb-item sj-item active"
                    aria-current="page"
                  >
                    {data?.code || ''}
                  </li>
                </ol>
              </nav>
            </div>
          </Col>
        </Row>

        <Card style={cardFilterStyle}>
          <Row>
            {renderInfoItem('WAREHOUSE', data?.source, 6)}
            {renderInfoItem(
              'TYPE',
              data?.type ? firstLetterUpperCase(data?.type) : '',
              6
            )}
          </Row>
        </Card>

        <Card style={cardFilterStyle}>
          <Row>
            <Col>
              <h5
                className="fw-bold text-uppercase mb-3"
                style={{ fontSize: '0.9rem', color: '#6c757d' }}
              >
                LIST TASK
              </h5>
              <Table
                hover
                responsive
                className="bg-white"
                style={{
                  fontSize: '13px',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  marginBottom: 0,
                  border: '1px solid #e9ecef'
                }}
              >
                <thead style={{ backgroundColor: '#f8f9fa' }}>
                  <tr>
                    {columnRacks.map((column) => (
                      <th
                        className={`align-middle p-3 ${column.className || ''}`}
                        key={column.key}
                        style={{ ...tableHeaderStyle, borderTop: 'none' }}
                      >
                        <span>{column.label}</span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data?.items?.length === 0 && (
                    <tr>
                      <td
                        colSpan={columnRacks.length}
                        className="p-3 text-center"
                      >
                        No data
                      </td>
                    </tr>
                  )}
                  {data?.items?.length > 0 &&
                    data.items.map((item, index) => {
                      return (
                        <React.Fragment key={`${item.rack_id}-${index}`}>
                          <tr>
                            {columnRacks.map((column) => (
                              <td
                                key={column.key}
                                className={`align-middle p-3 ${
                                  column.className || ''
                                }`}
                                style={{ borderBottom: '1px solid #e9ecef' }}
                              >
                                {column.component
                                  ? column.component(item)
                                  : item[column.key]}
                              </td>
                            ))}
                          </tr>
                          {collapsible[item.rack_id] && (
                            <tr>
                              <td
                                colSpan={columnRacks.length}
                                className="p-0"
                                style={{ borderBottom: '1px solid #e9ecef' }}
                              >
                                <div className="p-0 bg-white">
                                  <Table responsive className="mb-0">
                                    <thead
                                      style={{ backgroundColor: '#fdfdfd' }}
                                    >
                                      <tr>
                                        {columnSlots.map((column) => (
                                          <th
                                            className={`align-middle px-4 py-2 ${
                                              column.className || ''
                                            }`}
                                            key={column.key}
                                            style={{
                                              fontSize: '12px',
                                              fontWeight: '600',
                                              color: '#6c757d',
                                              borderBottom: '1px solid #e9ecef',
                                              borderTop: 'none'
                                            }}
                                          >
                                            <span>{column.label}</span>
                                          </th>
                                        ))}
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {item.slots?.length === 0 && (
                                        <tr>
                                          <td
                                            colSpan={columnSlots.length}
                                            className="px-4 py-3 text-center"
                                            style={{ borderBottom: 'none' }}
                                          >
                                            No data
                                          </td>
                                        </tr>
                                      )}
                                      {item.slots?.length > 0 &&
                                        item.slots.map(
                                          (slotItem, slotIndex) => (
                                            <tr
                                              key={`${slotItem.id}-${slotIndex}`}
                                            >
                                              {columnSlots.map((column) => (
                                                <td
                                                  key={column.key}
                                                  className={`align-middle px-4 py-3 ${
                                                    column.className || ''
                                                  }`}
                                                  style={{
                                                    fontSize: '13px',
                                                    borderBottom:
                                                      slotIndex ===
                                                      item.slots.length - 1
                                                        ? 'none'
                                                        : '1px solid #e9ecef'
                                                  }}
                                                >
                                                  {column.component
                                                    ? column.component(slotItem)
                                                    : slotItem[column.key]}
                                                </td>
                                              ))}
                                            </tr>
                                          )
                                        )}
                                    </tbody>
                                  </Table>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      )
                    })}
                </tbody>
              </Table>
            </Col>
          </Row>
        </Card>

        <Card style={cardFilterStyle}>
          <Row>
            {renderInfoItem('CREATED BY', data?.created_by, 3)}
            {renderInfoItem(
              'CREATED AT',
              !data?.created_at
                ? '-'
                : convertDateWithTime(data?.created_at, 'DD-MM-YYYY, HH:mm'),
              3
            )}
            {renderInfoItem('LAST UPDATE BY', data?.updated_by, 3)}
            {renderInfoItem(
              'LAST UPDATE AT',
              !data?.updated_at
                ? '-'
                : convertDateWithTime(data?.updated_at, 'DD-MM-YYYY, HH:mm'),
              3
            )}
          </Row>
        </Card>
      </Container>
    </>
  )
}

export default DetailReplenishmentPage
